import React, { useEffect, useState } from 'react';

import { makeStyles } from 'tss-react/mui';
import { TFunction, useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  ContentTemplate,
  Divider,
  Grid,
  HtmlTrans,
  Progress,
  SectionHeader,
  Typography,
} from 'web-library';
import { useQueryClient } from 'react-query';
import RouteKey from '../../../routes/key';
import Declaration from '../../../components/Declaration';
import PaymentButton, { PaymentMethod } from '../../payment/components/PaymentButton';
import { fallbackEmptyString } from '../../../utils/StringUtils';
import { candidateService } from '../../../api';
import PersonalDetails from './particulars/PersonalDetails';
import ContactDetails from './particulars/ContactDetails';
import ExamReviewSubjects from './ExamReviewSubjects';
import ExamFeeDetails from '../../../components/ExamFeeDetails';
import ExamDraftMessage from './ExamDraftMessage';
import { useExamNavigation } from '../../../routes/ExamRoute';
import { GET_EXAM_REGISTRATION } from '../../../api/candidate/exam/@candidate/queries-services/keys';
import NextOfKinContactDetails from './particulars/NextOfKinContactDetails';
import { useGetPaymentAccess } from '../../../api-refactored/financial/useGetPaymentAccess';

const useStyles = makeStyles()((theme) =>
({
  container: {
    alignItems: 'center',
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  dialogBold: {
    fontWeight: 'bold',
  },
  paymentButton: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
}));

const { receipt, examRegistrations } = RouteKey.exam.child;
const { serverError } = RouteKey.error.child;
const { exam, error } = RouteKey.child;

interface ExamReviewPathParams {
  uuid?: string;
}

interface ErrorMap {
  [key: string]: string | undefined;
}

const declarationContent = (t: TFunction<'translation'>) => (
  <HtmlTrans i18nKey="exam->>ReviewExam.CandidateConfirmationDeclaration" />
);

const ExamRegistrationAndProfile = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const [errMsg, setErrMsg] = useState<boolean>(false);
  const [isDeclared, setIsDeclared] = useState<boolean>(false);
  const [isPaymentDisabled, setIsPaymentDisabled] = useState(false);
  const { toSelectSubject, toRegisterExamInstruction } = useExamNavigation();
  const queryClient = useQueryClient();

  const [paymentAccessDetailsOption, setPaymentAccessDetailsOption] = useState<
    Array<PaymentMethod>
  >([]);

  const { uuid = '' } = useParams<ExamReviewPathParams>();

  const handleAgreement = () => {
    setErrMsg(false);
    setIsDeclared(!isDeclared);
  };

  const onBack = () => {
    history.goBack();
  };

  const {
    isLoading,
    mutate: examRegistrationProfileMutate,
    data: examReviewData,
  } = candidateService.exam.candidate.usePostExamRegistrationCandidateProfile(
    uuid,
    () => {
      queryClient.refetchQueries(GET_EXAM_REGISTRATION);
      refetchPaymentAccess().then(() => null);
    },
    (err) => {
      const examType = (err?.response?.data?.data?.code as string) || '';

      const errorMapping: ErrorMap = {
        '5008': toSelectSubject.replace(':uuid', uuid),
        '5011': toRegisterExamInstruction.replace(':examType', examType),
        '5012': `/${exam}/${examRegistrations.route}`,
        '1010': `/${error}/${serverError}`,
        '1605': `/${error}/${serverError}`,
      };
      const errorCode = err.response?.data.code;

      history.push(`${errorMapping[errorCode as string]}`);
    }
  );

  const { refetch: refetchPaymentAccess } = useGetPaymentAccess({
    onSuccess: (res) => {
      const paymentAccessDetailsArray = [];
      if (
        res.bankTransferMethod &&
        examReviewData &&
        examReviewData.data.payment.bankTransferOption
      ) {
        paymentAccessDetailsArray.push(PaymentMethod.BANK_TRANSFER);
      }
      if (res.creditCardMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.ENETS_CREDIT_CARD);
      }
      if (res.payNowMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.PAYNOW_QR);
      }
      setPaymentAccessDetailsOption(paymentAccessDetailsArray);
    },
  });

  useEffect(() => {
    if (!examReviewData) {
      examRegistrationProfileMutate(null);
    }
  }, [examRegistrationProfileMutate, examReviewData]);

  /** to save candidate after payment is successful
   * */
  const candidateExamPayment = candidateService.exam.candidate.usePostExamProceedToPayment();

  const handlePaymentOption = (
    callback: (internalTransactionRef: string) => void,
    method?: string
  ) => {
    candidateExamPayment.mutate(
      {
        uuid,
        paymentMode: fallbackEmptyString(method),
      },
      {
        onSuccess: (res) => {
          if (res.data?.paymentSuccess === true) {
            history.push(`/${exam}/${examRegistrations.route}/${uuid}/${receipt.route}`);
          }
          callback(res.data.internalTransactionRef);
        },
        onError: () => {
          callback('');
        },
      }
    );
  };

  const handleClickPaymentButton = (callback: () => void) => {
    if (!isDeclared) {
      setErrMsg(true);
    } else {
      setErrMsg(false);
      callback();
    }
  };

  if (isLoading) {
    return (
      <ContentTemplate transparent continuous>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      {examReviewData && (
        <Box>
          <ContentTemplate isContinuable type="full">
            <SectionHeader title={t('exam->>ReviewExam.Confirm Particulars')} />
            <PersonalDetails
              examCandidateDetail={examReviewData.data.candidateDetails}
              defaultExpanded
            />
            <ContactDetails contactDetails={examReviewData.data.candidateDetails} />
            <NextOfKinContactDetails {...examReviewData.data.candidateDetails} />
          </ContentTemplate>

          <ContentTemplate isContinuable type="full">
            <ExamReviewSubjects {...examReviewData.data} />
            <ExamFeeDetails
              gstRegistrationNumber={examReviewData.data.gstRegistrationNumber}
              groupedLevelFees={examReviewData.data.groupedLevelFees}
              payment={examReviewData.data.payment}
            />
          </ContentTemplate>

          <ContentTemplate isContinuable type="full">
            <Divider />
            <Declaration
              isAgreed={isDeclared}
              handleAgreement={handleAgreement}
              customContent={declarationContent(t)}
            />
            {errMsg && (
              <Grid xs={12} md={8}>
                <Typography className={classes.errorText} display="inline" variant="body2">
                  {t('components->>Declaration.Please confirm declaration before you proceed.')}
                </Typography>
              </Grid>
            )}
          </ContentTemplate>
          <ContentTemplate continuous>
            <Grid container direction="row" alignItems="flex-start" spacing={3}>
              <Grid xs={12} md={3}>
                <Button
                  id="button-selectSubject-back"
                  fullWidth
                  // className={classes.button}
                  variant="outlined"
                  onClick={() => {
                    onBack();
                  }}
                >
                  {t('exam->>ReviewExam.Back')}
                </Button>
              </Grid>
              <Grid xs={12} md={4}>
                <PaymentButton
                  buttonClassName={classes.paymentButton}
                  isUniqueLink={false}
                  disabled={isPaymentDisabled}
                  onPaymentComplete={(isRetry) => {
                    setIsPaymentDisabled(isRetry);
                  }}
                  paymentMethodsAvailable={paymentAccessDetailsOption}
                  onClickPaymentButton={(callback) => {
                    handleClickPaymentButton(callback);
                  }}
                  onHandlePaymentMethod={(callback, method) => {
                    handlePaymentOption(callback, method);
                  }}
                  onSuccessPayment={() =>
                    history.push(`/${exam}/${examRegistrations.route}/${uuid}/${receipt.route}`)
                  }
                  fullWidth
                  buttonText={t('exam->>ReviewExam.ConfirmAndPay')}
                  buttonVariant="contained"
                />
              </Grid>
            </Grid>
            <ExamDraftMessage enableApi={false} />
          </ContentTemplate>
        </Box>
      )}
    </>
  );
};
export default ExamRegistrationAndProfile;
